import { toast } from "react-toastify";
import { clearAllCaches } from "./requestCache";
import { clearStoredTenantData } from "./tenantState";

const AUTH_STORAGE_KEYS = [
  "token",
  "accessToken",
  "refreshToken",
  "authEmail",
  "user",
  "userId",
  "username",
  "currentUserName",
  "activeTenantName",
  "permissionRules",
  "subscriptionReadOnly",
  "studentProfileCache",
  "teacherProfileCache",
  "adminDashboardPrefs",
  "schoolDashboardPrefs",
  "studentDashboardPrefs",
  "teacherDashboardPrefs",
  "superAdminDashboardPrefs",
];

let terminationInProgress = false;
let lastTerminationAt = 0;

const safeStorageRemove = (storage, key) => {
  try {
    storage.removeItem(key);
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }
};

export const clearSessionData = () => {
  AUTH_STORAGE_KEYS.forEach((key) => safeStorageRemove(window.localStorage, key));

  try {
    const tenantLogoKeys = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (key?.startsWith("tenant:logo:")) tenantLogoKeys.push(key);
    }
    tenantLogoKeys.forEach((key) => safeStorageRemove(window.localStorage, key));
  } catch {
    // Ignore unavailable storage enumeration.
  }

  try {
    window.sessionStorage.clear();
  } catch {
    // Ignore unavailable session storage.
  }

  clearStoredTenantData();
  clearAllCaches();
};

const buildLoginUrl = (reason = "expired") => {
  const basePath = `${window.location.origin}${process.env.PUBLIC_URL || ""}/login`;
  return `${basePath}?reason=${encodeURIComponent(reason)}`;
};

export const getStoredAuthSnapshot = () => {
  let role = "";
  let savedUser = "";

  try {
    savedUser = window.localStorage.getItem("user") || window.sessionStorage.getItem("user") || "";
    if (savedUser) {
      try {
        const parsed = JSON.parse(savedUser);
        role = String(parsed?.role ?? parsed ?? "").trim();
      } catch {
        role = savedUser.trim();
      }
    }
  } catch {
    return { role: "", hasSession: false, isExpired: false };
  }

  return {
    role,
    // This is UI metadata only. App verifies the HttpOnly-cookie session with
    // the backend before treating a user as authenticated.
    hasSession: Boolean(role),
    isExpired: false,
  };
};

export const terminateSession = ({
  reason = "expired",
  message = "Your session has expired. Please login again.",
  redirect = true,
  notify = true,
} = {}) => {
  const now = Date.now();
  if (terminationInProgress || now - lastTerminationAt < 1000) return;

  terminationInProgress = true;
  lastTerminationAt = now;
  clearSessionData();

  try {
    window.dispatchEvent(new CustomEvent("auth:logout", {
      detail: { reason, message },
    }));
  } catch {
    // Ignore event dispatch failures during teardown.
  }

  if (notify && reason !== "manual") {
    toast.error(message, {
      position: "top-center",
      autoClose: 5000,
    });
  }

  if (redirect && window.location.pathname !== "/login") {
    try {
      window.location.replace(buildLoginUrl(reason));
    } catch {
      // Keep the local session invalidated even if navigation is unavailable.
    }
  }

  window.setTimeout(() => {
    terminationInProgress = false;
  }, 0);
};
